import { useCallback, useMemo, useState } from "react"

import type { StartField } from "../utils/workflow-run-utils"

import {
  buildTestRunUserInputData,
  resolveTestRunUserInputNodes,
  type TestRunUserInputNode,
  type TestRunUserInputPayload,
} from "../utils/workflow-test-run-user-inputs"

type NodeLike = { id: string; type?: string; data?: unknown }

/**
 * Test-panel state for `user_input_data` (#2964): the answers for mid-flow
 * User Input nodes (Test Run cannot pause there, so they are asked up front)
 * and the builder that merges them with the START-node text fields. Pure
 * rules live in `utils/workflow-test-run-user-inputs.ts`.
 */
export function useTestRunUserInputs(params: {
  nodes: readonly NodeLike[]
  useFieldForm: boolean
  textFields: readonly StartField[]
  fieldTextValues: Record<string, string | undefined>
  inputValue: string
}): {
  userInputNodes: TestRunUserInputNode[]
  userInputAnswers: Record<string, string | undefined>
  setUserInputAnswer: (inputId: string, value: string) => void
  clearUserInputAnswers: () => void
  buildUserInputData: () => TestRunUserInputPayload
} {
  const { nodes, useFieldForm, textFields, fieldTextValues, inputValue } = params
  const [userInputAnswers, setUserInputAnswers] = useState<Record<string, string | undefined>>({})
  const userInputNodes = useMemo(() => resolveTestRunUserInputNodes(nodes), [nodes])

  const setUserInputAnswer = useCallback((inputId: string, value: string) => {
    setUserInputAnswers((prev) => ({ ...prev, [inputId]: value }))
  }, [])
  const clearUserInputAnswers = useCallback(() => setUserInputAnswers({}), [])

  const buildUserInputData = useCallback(
    () =>
      buildTestRunUserInputData({
        useFieldForm,
        textFieldKeys: textFields.map((field) => field.key),
        fieldTextValues,
        inputValue,
        userInputNodes,
        userInputAnswers,
      }),
    [useFieldForm, textFields, fieldTextValues, inputValue, userInputNodes, userInputAnswers]
  )

  return {
    userInputNodes,
    userInputAnswers,
    setUserInputAnswer,
    clearUserInputAnswers,
    buildUserInputData,
  }
}
